import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { ProviderReasonForVisit } from '@/types/settings-api';

/**
 * Canonical React Query key for a practitioner's reason-for-visit list.
 */
export function providerReasonForVisitsQueryKey(
  providerId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'provider-reason-for-visits', providerId];
}

/**
 * Reason-for-visit options for a practitioner — PROD-1826 (appointment-profile
 * "Reason for visit" dropdown).
 *
 *   GET /api/ProfessionalDetails/GetProviderReasonForVisits/{providerId}
 *
 * Each option's `code` is a reason-record GUID (NOT a clinical code) and
 * `display` is the reason text; the profile save stores the selection as
 * `reasonCode: { code, definition, display }`.
 *
 * Flutter source: `common_data_source_impl.dart:466-484` (`appointmentReasonValueSets`
 * → `providerAPI(ProfessionalDetails)` base). Endpoint path verified against a
 * live dev cURL 2026-08-06 (matches the Flutter-derived path verbatim).
 *
 * Lives in `shared/hooks/` (not a domain folder) because BOTH domains consume it:
 * Settings (`appointment-profile-edit-dialog`) configures the list, and Scheduling
 * (`appointment-profile-section`) offers it as the appointment's RFV picker.
 * Flutter dispatches the same `AppointmentReasonValueSetEvent` from the scheduling
 * create/edit widget (`create_appointment_widget.dart:2182`) — the RFV list is NOT
 * exclusive to the settings surface, and treating it as such is what left the
 * scheduling picker dependent on the PreBook chain alone. `settings/services/`
 * re-exports this module so existing settings imports are unchanged.
 */
export function useProviderReasonForVisits(
  providerId: string | null | undefined,
) {
  return useQuery({
    queryKey: providerReasonForVisitsQueryKey(providerId),
    queryFn: ({ signal }) => {
      if (!providerId) {
        throw new Error('useProviderReasonForVisits called without providerId');
      }
      return get<ProviderReasonForVisit[]>(
        `/api/ProfessionalDetails/GetProviderReasonForVisits/${providerId}`,
        { signal },
      );
    },
    enabled: !!providerId,
    staleTime: 60_000,
  });
}
